import { LOOK_KEY_PATTERN, isCharacterVoice, isOfferedInvitation, nameStepNeeds, type Appearance, type NameStepField, type OfferedInvitation } from "odyssey-shared";
import { say } from "../words.ts";
import { announceLogout } from "./auth-channel.ts";
import { post } from "./auth-request.ts";
import type { RosterCharacter } from "./select-state.ts";

/**
 * The game page's half of account auth (P1.4-T2, P1.4-T18, P1.4-T20, P1.4-T26, P1.4-T6, P1.4-T7): the
 * account endpoints of the gateway and the grant kept in `sessionStorage`, so two accounts can play in two
 * tabs of one browser; the timer that trades the refresh token for a new session token before the old one
 * expires is `keep-fresh.ts`, and resuming a kept device is `resume.ts`. An
 * account is an email and a password (ADR-064's amendment "accounts have no account name") holding one family
 * name, and each character has a first name (ADR-065). The session token is what the page presents in
 * `hello.zoneToken`; nothing here ever writes a password anywhere but the request body. An account that lacks
 * its family name or its first character is granted its refresh token alone, which the name step posts with
 * the names its `needs` asks for (ADR-065 decision 3); a gateway from before families sends no `needs`, and
 * the step then asks for the character's name alone, as it did. A login's grant lists the account's
 * characters, which character select shows, and choosing one is a refresh naming it by id; the tab keeps
 * that id with its session and names it in every refresh after, so a reload plays the same character
 * (ADR-065 decision 6). While the account holds fewer characters than its limit, character select creates a
 * further one through a route of its own, which answers the session for it (P1.4-T28). The `accountName` and
 * `username` fields a gateway still sends are neither read nor kept, and `familyName`, `needs`,
 * `characterId`, `characters` and `characterLimit` are read only when present, so the page plays the
 * account's first character against a gateway from before character select, as it did, and offers no
 * creation against one from before the limit.
 */

/**
 * What a login, a refresh or the name step gave the page for a character that may play: its first name,
 * which the wire calls `characterName`; its family name, absent from a gateway before families; its id,
 * absent from a gateway before character select, which a refresh names so it mints for the same character; and
 * its look as the page last knew it (ADR-094 note 87: the character list's, or the one chosen at creation), absent
 * when the page knows none, which the world boot loads the sheets of beside the fallback look's; and its Party invitations
 * setting (ADR-101 decision 6) when it is one the page offers other than the default, absent for On my map.
 */
export interface Session {
  readonly sessionToken: string;
  readonly expiresAt: number;
  readonly refreshToken: string;
  readonly characterName: string;
  readonly familyName?: string;
  readonly characterId?: string;
  readonly look?: Appearance;
  readonly partyInvites?: OfferedInvitation;
}

/**
 * The account's characters as a login or a refresh lists them for character select, in creation order, and
 * its character limit, null from a gateway before the limit (P1.4-T27), which offers no creation.
 */
export interface Roster {
  readonly characters: readonly RosterCharacter[];
  readonly limit: number | null;
}

/**
 * What a login or a refresh gave the page for an account that needs a name: the session's refresh token,
 * which the name step posts; the fields the step asks for, absent from a gateway before families, which asks
 * for the character's name alone; and the account's family name when it has one. It admits nobody to the
 * game.
 */
export interface Naming {
  readonly refreshToken: string;
  readonly needs?: readonly NameStepField[];
  readonly familyName?: string;
}

/**
 * What the name step posts beside the refresh token: the fields its `needs` asks for, each trimmed, the voice picked
 * for the character it makes, absent for none (P1.9-T83), and the look chosen on the Look step, absent for the
 * gateway's default (ADR-094 decision 23).
 */
export interface NameStepNames {
  readonly familyName?: string;
  readonly characterName?: string;
  readonly voice?: string;
  readonly look?: Appearance;
}

/**
 * Either grant, told apart by kind. A playing grant from a login or a refresh of a gateway with character
 * select carries the account's roster, which is never stored with the session.
 */
export type Grant = { readonly kind: "playing"; readonly session: Session; readonly roster?: Roster } | { readonly kind: "naming"; readonly naming: Naming };

/**
 * The outcome of a login: the grant, or a sentence to show the player.
 */
export type AuthOutcome = { readonly ok: true; readonly grant: Grant } | { readonly ok: false; readonly message: string };

/**
 * The outcome of the name step: the session for the character that stands, or a sentence to show the
 * player, the field the gateway named (its code `familyName` or `characterName`), or null when its answer
 * named neither, and whether the session behind the step is gone, in which case the player logs in again.
 */
export type NameOutcome = { readonly ok: true; readonly session: Session } | { readonly ok: false; readonly message: string; readonly field: NameStepField | null; readonly lost: boolean };

/**
 * The three keys of a look as a request carries them, and nothing else.
 *
 * @param look The look.
 * @returns The request's `look`.
 */
function lookField(look: Appearance): Record<string, string> {
  return { skin: look.skin, hair: look.hair, clothes: look.clothes };
}

/**
 * A field of the account forms that the gateway names when it refuses a registration for it: an email out
 * of shape or already registered, or a password out of shape.
 */
export type CredentialField = "email" | "password";

/**
 * The outcome of a registration, which creates no character and no session: success, or a sentence to show
 * the player and the field the gateway refused, or null when its answer named neither field (a rate limit,
 * a busy server, a clash, no answer).
 */
export type RegisterOutcome = { readonly ok: true } | { readonly ok: false; readonly message: string; readonly field: CredentialField | null };

/**
 * The outcome of logging out everywhere: done, or a sentence to show the player and whether this tab's
 * own session is gone, in which case the player logs in again.
 */
export type LogoutEverywhereOutcome = { readonly ok: true } | { readonly ok: false; readonly message: string; readonly lost: boolean };

/**
 * The outcome of a logout: done, or a sentence to show the player, for a logout the gateway did not answer or
 * refused, which ended nothing.
 */
export type LogoutOutcome = { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * The outcome of choosing a character: the session for it, or a sentence to show the player, the gateway's code for
 * the refusal (empty when the answer was not a refusal the gateway worded) and whether the session behind the choice
 * is gone, in which case the player logs in again.
 */
export type ChooseOutcome = { readonly ok: true; readonly session: Session } | { readonly ok: false; readonly message: string; readonly code: string; readonly lost: boolean };

/**
 * The outcome of creating a character (P1.4-T28): the session for the new character, or a sentence to show
 * the player, what the gateway refused (`characterName` for the first name, `limit` for an account at its
 * limit, or null when its code named neither) and whether the session behind it is gone.
 */
export type CreateOutcome = { readonly ok: true; readonly session: Session } | { readonly ok: false; readonly message: string; readonly refused: "characterName" | "limit" | null; readonly lost: boolean };

const STORAGE_KEY = "odyssey.session";

const UNREACHABLE = say("account.request.unreachable");

/**
 * The origin of the account endpoints, derived from the gateway's WebSocket URL so the page needs no second
 * configuration value.
 *
 * @param socketUrl The gateway URL the page connects to.
 * @returns The HTTP origin of the same host, or an empty string when the URL is unusable.
 */
export function authBase(socketUrl: string): string {
  try {
    const url = new URL(socketUrl);
    return `${url.protocol === "wss:" ? "https:" : "http:"}//${url.host}`;
  } catch {
    return "";
  }
}

/**
 * The grant this tab holds.
 *
 * @returns The grant, or null when none is stored or the store is unavailable.
 */
export function loadGrant(): Grant | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return grantFrom(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Stores or clears this tab's grant. A naming grant is kept too, so a reload before naming returns to the
 * name step rather than to the login form.
 *
 * @param grant The grant, or null to forget it.
 */
export function storeGrant(grant: Grant | null): void {
  try {
    if (grant === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(grant.kind === "playing" ? grant.session : grant.naming));
  } catch {
    return;
  }
}

/**
 * Registers an account by its email and password. No character is created until the name step.
 *
 * @param base The account origin.
 * @param fields Email and password.
 * @returns Success, or the server's sentence and the field it refused, read from the code the gateway
 *   answers with (`email` or `password`), or null when the code names neither.
 */
export async function register(base: string, fields: { readonly email: string; readonly password: string }): Promise<RegisterOutcome> {
  const answer = await post(base, "register", { email: fields.email, password: fields.password });
  if (answer.ok) {
    return { ok: true };
  }
  return { ok: false, message: answer.message, field: answer.code === "email" || answer.code === "password" ? answer.code : null };
}

/**
 * Logs in with an email address, which the gateway reads from the `identifier` field. With "Keep me logged in"
 * ticked the login is sent with credentials and posts `keep`, so the browser takes the kept session's cookie
 * the gateway answers with and sends the one it may already hold, which the new one replaces (ADR-075).
 *
 * @param base The account origin.
 * @param fields The email as `identifier`, the password, and whether to keep this device logged in.
 * @returns The grant, which is a naming grant for an account that needs its family name or its first
 *   character, or the server's sentence.
 */
export async function login(base: string, fields: { readonly identifier: string; readonly password: string; readonly keep?: boolean }): Promise<AuthOutcome> {
  const keep = fields.keep === true;
  const answer = await post(base, "login", keep ? { identifier: fields.identifier, password: fields.password, keep: true } : { identifier: fields.identifier, password: fields.password }, keep);
  if (!answer.ok) {
    return { ok: false, message: answer.message };
  }
  const grant = grantFrom(answer.body);
  return grant !== null ? { ok: true, grant } : { ok: false, message: UNREACHABLE };
}

/**
 * Names what the account lacks through the name step: its family name, its first character's first name,
 * or both, as the naming grant's `needs` asked. The answer names the family and the character that stand,
 * which are another tab's choices when that tab named them first.
 *
 * @param base The account origin.
 * @param naming The naming grant this tab holds.
 * @param names The names chosen, only the fields the step asks for.
 * @returns The session, or the server's sentence, the field it named and whether the session is gone.
 */
export async function nameCharacter(base: string, naming: Naming, names: NameStepNames): Promise<NameOutcome> {
  const body: Record<string, string | Readonly<Record<string, string>>> = {
    refreshToken: naming.refreshToken,
    ...(names.familyName === undefined ? {} : { familyName: names.familyName }),
    ...(names.characterName === undefined ? {} : { characterName: names.characterName }),
    ...(names.voice === undefined ? {} : { voice: names.voice }),
    ...(names.look === undefined ? {} : { look: lookField(names.look) }),
  };
  const answer = await post(base, "character", body);
  if (!answer.ok) {
    const field = answer.code === "familyName" || answer.code === "characterName" ? answer.code : null;
    return { ok: false, message: answer.message, field, lost: answer.status === 401 };
  }
  const grant = grantFrom(answer.body);
  return grant?.kind === "playing" ? { ok: true, session: grant.session } : { ok: false, message: UNREACHABLE, field: null, lost: false };
}

/**
 * What one refresh came to: the grant it was given; a refusal, the gateway's 401, which is how it says the sign-in
 * has expired, was ended or is not for that character; or no grant for a reason the server may mend, which is no
 * answer or none in time, a server error, a busy server, a limit, or a body the page cannot read.
 */
export type RefreshAttempt = { readonly kind: "granted"; readonly grant: Grant } | { readonly kind: "refused" } | { readonly kind: "failed" };

/**
 * Trades a refresh token for a new grant, for the character the tab plays when it names one, so a reload and
 * the kept-fresh session stay on it, or for the account's first when it names none, and says why when no grant
 * came.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @param characterId The id of the character the tab plays, or undefined when it has none, as a session from
 *   a gateway before character select has none.
 * @returns The grant, which is a naming grant while the account needs its family name or its first character;
 *   `refused` when the gateway answered 401; or `failed` for anything else.
 */
export async function renew(base: string, refreshToken: string, characterId?: string): Promise<RefreshAttempt> {
  const answer = await post(base, "refresh", characterId === undefined ? { refreshToken } : { refreshToken, characterId });
  if (!answer.ok) {
    return answer.status === 401 ? { kind: "refused" } : { kind: "failed" };
  }
  const grant = grantFrom(answer.body);
  return grant === null ? { kind: "failed" } : { kind: "granted", grant };
}

/**
 * Trades a refresh token for a new grant, as `renew` does, when only the grant matters.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @param characterId The id of the character the tab plays, or undefined when it has none.
 * @returns The grant, or null when none came, whatever the reason.
 */
export async function refresh(base: string, refreshToken: string, characterId?: string): Promise<Grant | null> {
  const attempt = await renew(base, refreshToken, characterId);
  return attempt.kind === "granted" ? attempt.grant : null;
}

/**
 * Chooses the character to play: a refresh that names it by id, which mints the session token for it
 * (ADR-065 decision 6).
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @param characterId The chosen character's id.
 * @returns The session for it, or the server's sentence and whether the session is gone, which the gateway
 *   answers when the refresh token names no live session or the character is not the account's.
 */
export async function chooseCharacter(base: string, refreshToken: string, characterId: string): Promise<ChooseOutcome> {
  const answer = await post(base, "refresh", { refreshToken, characterId });
  if (!answer.ok) {
    return { ok: false, message: answer.message, code: answer.code, lost: answer.status === 401 };
  }
  const grant = grantFrom(answer.body);
  return grant?.kind === "playing" ? { ok: true, session: grant.session } : { ok: false, message: UNREACHABLE, code: "", lost: false };
}

/**
 * Creates a further character for the account (P1.4-T28, ADR-065 decision 6): its first name, posted with the
 * refresh token to the route of its own, which answers the session for the new character.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @param firstName The new character's first name, trimmed.
 * @param voice The voice chosen for it (P1.9-T83), or null for none, which sends no voice.
 * @param look The look chosen on the Look step (ADR-094 decision 23), or null for the gateway's default.
 * @returns The session for the new character, or the server's sentence, what it refused and whether the
 *   session is gone.
 */
export async function createCharacter(base: string, refreshToken: string, firstName: string, voice: string | null = null, look: Appearance | null = null): Promise<CreateOutcome> {
  const answer = await post(base, "create-character", { refreshToken, characterName: firstName, ...(voice === null ? {} : { voice }), ...(look === null ? {} : { look: lookField(look) }) });
  if (!answer.ok) {
    const refused = answer.code === "characterName" || answer.code === "limit" ? answer.code : null;
    return { ok: false, message: answer.message, refused, lost: answer.status === 401 };
  }
  const grant = grantFrom(answer.body);
  return grant?.kind === "playing" ? { ok: true, session: grant.session } : { ok: false, message: UNREACHABLE, refused: null, lost: false };
}

/**
 * What changing a look came to: accepted, or why not. `look`, `voice`, `rate` and `frozen` are the gateway's refusals
 * (400, 400, 429 and 403), each with the gateway's sentence, and `look` with the field it refused when the answer named one;
 * `lost` is a session that is gone (401), which has no sentence since the page leaves the world for it; `unanswered`
 * is everything else: no answer, a gateway without the route (404), or an answer that is not the gateway's.
 */
export type LookOutcome = { readonly ok: true } | { readonly ok: false; readonly why: "look" | "voice" | "rate" | "frozen" | "unanswered"; readonly message: string; readonly field: "skin" | "hair" | "clothes" | null } | { readonly ok: false; readonly why: "lost" };

/**
 * Changes a character's look (ADR-094's amendment of 2026-10-06, B4), and its voice with it when one is given (note
 * 126): the refresh token, the character, the three keys and the voice, empty for none, posted to `character-look`.
 * The gateway stores what it accepts and tells the world, which shows the look; the voice is heard from the
 * character's next join. The answer is read only for whether it was accepted.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @param characterId The character's id.
 * @param look The look's three keys.
 * @param voice The voice, null for none; undefined sends none, which leaves the stored voice.
 * @returns Accepted, or why not.
 */
export async function changeLook(base: string, refreshToken: string, characterId: string, look: Appearance, voice?: string | null): Promise<LookOutcome> {
  const answer = await post(base, "character-look", { refreshToken, characterId, look: lookField(look), ...(voice === undefined ? {} : { voice: voice ?? "" }) });
  if (answer.ok) {
    const accepted = typeof answer.body === "object" && answer.body !== null && (answer.body as { characterId?: unknown }).characterId === characterId;
    return accepted ? { ok: true } : { ok: false, why: "unanswered", message: UNREACHABLE, field: null };
  }
  if (answer.status === 401) {
    return { ok: false, why: "lost" };
  }
  const why = answer.status === 400 && answer.code === "look" ? "look" : answer.status === 400 && answer.code === "voice" ? "voice" : answer.status === 429 && answer.code === "rate" ? "rate" : answer.status === 403 && answer.code === "frozen" ? "frozen" : "unanswered";
  return { ok: false, why, message: answer.message, field: why === "look" ? (answer.field ?? null) : null };
}

/**
 * Revokes the session and forgets it. It is sent with credentials, so the gateway also ends this device's keep
 * for the account and clears its cookie, and once it has, the other tabs of this browser are told to check
 * their own sessions (ADR-075 decision 6). A logout the gateway did not answer, or refused, changed nothing on
 * the server and forgets nothing here; it says why, so the page can offer a retry (`logout-retry.tsx`) rather
 * than show the log in form as if the device were forgotten.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @returns Done, or the sentence to show.
 */
export async function logout(base: string, refreshToken: string): Promise<LogoutOutcome> {
  const answer = await post(base, "logout", { refreshToken }, true);
  if (!answer.ok) {
    return { ok: false, message: answer.message };
  }
  storeGrant(null);
  announceLogout();
  return { ok: true };
}

/**
 * Ends every session of the account, this tab's included, and forgets this tab's grant once the gateway
 * has; it is sent with credentials, so the gateway clears this device's kept cookie too, and the other tabs
 * of this browser are told to check their own sessions. A failure forgets nothing and says why, and whether
 * this tab's own session is already gone, which the gateway answers when the refresh token names no live
 * session: then no other session was ended.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @returns Success, or the server's sentence and whether this tab's session is gone.
 */
export async function logoutEverywhere(base: string, refreshToken: string): Promise<LogoutEverywhereOutcome> {
  const answer = await post(base, "logout-everywhere", { refreshToken }, true);
  if (!answer.ok) {
    return { ok: false, message: answer.message, lost: answer.status === 401 };
  }
  storeGrant(null);
  announceLogout();
  return { ok: true };
}

/**
 * The name the HUD holds for the session a page plays, in its two parts: its character's first name and the family
 * name, empty from a gateway before families. A `?token=` page has no grant to name its character, so it shows a
 * stand-in until `welcome` carries the name.
 *
 * @param session The account session, or null for a `?token=` page.
 * @returns The first name and the family name.
 * @example
 * hudName(null);
 * // { firstName: "dev token", familyName: "" }
 */
export function hudName(session: Session | null): { readonly firstName: string; readonly familyName: string } {
  return session === null ? { firstName: "dev token", familyName: "" } : { firstName: session.characterName, familyName: session.familyName ?? "" };
}

/**
 * The grant a parsed answer or stored grant describes: a session when it carries a session token, with the
 * account's roster when it lists the characters as character select reads them, and in it the account's
 * character limit when that is a whole number of 1 or more; the session takes the look the roster lists for its own
 * character, which the world boot loads the sheets of (ADR-094 note 87); and a naming grant when it
 * carries a refresh token and no session token, with the fields its `needs` names and its family name when
 * either is present. Any `accountName` or `username` beside them, as a gateway still sends and a page of an
 * earlier release stored in this tab, is ignored.
 *
 * @param value A parsed login, refresh or name step answer, or a grant read back from storage.
 * @returns The grant, or null when a field is missing or of the wrong type.
 */
export function grantFrom(value: unknown): Grant | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const { sessionToken, refreshToken, needs, familyName, characters, characterLimit } = value as Record<string, unknown>;
  if (sessionToken === undefined && typeof refreshToken === "string") {
    const asked = nameStepNeeds(needs);
    return { kind: "naming", naming: { refreshToken, ...(asked === null ? {} : { needs: asked }), ...(typeof familyName === "string" ? { familyName } : {}) } };
  }
  const session = sessionFrom(value);
  if (session === null) {
    return null;
  }
  const listed = rosterFrom(characters);
  const limit = typeof characterLimit === "number" && Number.isInteger(characterLimit) && characterLimit >= 1 ? characterLimit : null;
  const mine = listed?.find((character) => character.id === session.characterId);
  const played = { ...session, ...(mine?.look === undefined ? {} : { look: mine.look }), ...(mine?.partyInvites === undefined ? {} : { partyInvites: mine.partyInvites }) };
  return listed === null ? { kind: "playing", session: played } : { kind: "playing", session: played, roster: { characters: listed, limit } };
}

/**
 * The account's characters as a grant lists them: every entry an object carrying a string `id` and a string
 * `firstName`, in the order sent, its `voice` when it carries one this page lists (P1.9-T83), its `partyInvites` when it is
 * an offered value other than the default (P2.13-T16), and its `look` when it
 * carries three keys of a look's shape (ADR-094 decision 27); a character with none, with a voice a later gateway
 * knows, or from a gateway before voices reads as no voice, and one with no look of that shape lists none.
 *
 * @param value The grant's `characters`, as parsed from JSON.
 * @returns The characters, or null when the value is not such a list, which is how a grant from a gateway
 *   before character select, or one listing an entry of another shape, reads.
 */
function rosterFrom(value: unknown): RosterCharacter[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const listed: RosterCharacter[] = [];
  for (const entry of value as readonly unknown[]) {
    const { id, firstName, voice, look, partyInvites } = typeof entry === "object" && entry !== null ? (entry as Record<string, unknown>) : {};
    if (typeof id !== "string" || typeof firstName !== "string") {
      return null;
    }
    const worn = rosterLook(look);
    listed.push({ id, firstName, ...(isCharacterVoice(voice) ? { voice } : {}), ...(isOfferedInvitation(partyInvites) && partyInvites !== "map" ? { partyInvites } : {}), ...(worn === null ? {} : { look: worn }) });
  }
  return listed;
}

/**
 * A roster entry's look: three keys of a look's shape, each `LOOK_KEY_PATTERN`; whether the page's table holds them is
 * for whoever draws it.
 *
 * @param value The entry's `look`, as parsed from JSON.
 * @returns The look, or null when it is absent or of another shape.
 */
function rosterLook(value: unknown): Appearance | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const { skin, hair, clothes } = value as Record<string, unknown>;
  const keys = [skin, hair, clothes];
  return keys.every((key) => typeof key === "string" && LOOK_KEY_PATTERN.test(key)) ? { skin: skin as string, hair: hair as string, clothes: clothes as string } : null;
}

/**
 * The session a parsed grant or stored session describes, keeping only the fields the page uses: the family
 * name, the character's id and its look when the grant or the stored session carries them.
 *
 * @param value A parsed login or refresh answer, or a session read back from storage.
 * @returns The session, or null when a field is missing or of the wrong type.
 */
export function sessionFrom(value: unknown): Session | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const { sessionToken, expiresAt, refreshToken, characterName, familyName, characterId, look, partyInvites } = value as Record<string, unknown>;
  if (typeof sessionToken !== "string" || typeof expiresAt !== "number" || typeof refreshToken !== "string" || typeof characterName !== "string") {
    return null;
  }
  const worn = rosterLook(look);
  return { sessionToken, expiresAt, refreshToken, characterName, ...(typeof familyName === "string" ? { familyName } : {}), ...(typeof characterId === "string" ? { characterId } : {}), ...(worn === null ? {} : { look: worn }), ...(isOfferedInvitation(partyInvites) && partyInvites !== "map" ? { partyInvites } : {}) };
}
